import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Factory, ShieldCheck, Truck, CheckCircle, Circle, Warning, X, Check,
    ArrowsClockwise, ArrowSquareOut, Plus,
} from "@phosphor-icons/react";
import {
    PRODUCTION_STEP_LABELS, QC_TOGGLE_LABELS, fmt, humanize,
    fmtDate, fmtDateTime, EMPTY,
} from "./format";

/* ─── Section header + status pill ────────────────────────────────── */
const STATUS_TONES = {
    not_started: "bg-slate-100 text-slate-700",
    in_progress: "bg-amber-100 text-amber-800",
    complete:    "bg-emerald-100 text-emerald-800",
    failed:      "bg-rose-100 text-rose-800",
    awaiting:    "bg-slate-100 text-slate-600",
};

function SectionHeader({ Icon, title, status, statusLabel, testId }) {
    return (
        <div className="flex items-center justify-between gap-3 px-5 pt-5" data-testid={`${testId}-header`}>
            <h2 className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/85 inline-flex items-center gap-2">
                {Icon && <Icon size={12} weight="duotone" />}
                {title}
            </h2>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_TONES[status] || STATUS_TONES.not_started}`}
                  data-testid={`${testId}-status`}>
                {statusLabel}
            </span>
        </div>
    );
}

/* iter101.14 · Phase 3 — Restyled to match the doctor portal's
   SectionCard aesthetic (soft border, tinted bg, no divider). Padding
   remains a caller responsibility so we don't have to touch every
   `<div className="p-5">` body block. */
function SectionCard({ children, testId }) {
    return (
        <section
            className="rounded-xl border border-border/70 bg-secondary/15 overflow-hidden"
            data-testid={testId}
        >
            {children}
        </section>
    );
}

/* ─── Material resolver (re-uses inventory list) ───────────────────── */
function useMaterialName(materialId) {
    const [name, setName] = useState(null);
    useEffect(() => {
        if (!materialId) return;
        api.get("/admin/inventory/materials")
            .then((r) => {
                const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
                const hit = list.find((m) => m.id === materialId);
                setName(hit?.name || hit?.code || materialId);
            })
            .catch(() => setName(materialId));
    }, [materialId]);
    return name;
}

/* ═══════════════════════════════════════════════════════════════════
   SECTION A — PRODUCTION
   ═══════════════════════════════════════════════════════════════════ */
const PRODUCTION_STEP_KEYS = [
    "stl_files_received", "files_verified", "material_pulled",
    "print_started", "print_completed", "trim_and_finish", "ready_for_qc",
];

function ProductionSection({ c }) {
    const [checklist, setChecklist] = useState({});
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        api.get(`/admin/cases/${c.id}/production-checklist`)
            .then((r) => setChecklist(r.data.checklist || {}))
            .catch(() => {});
    }, [c.id]);

    const toggle = async (step) => {
        const newVal = !checklist[step];
        setBusy(step);
        try {
            const { data } = await api.put(
                `/admin/cases/${c.id}/production-checklist`,
                { [step]: newVal },
            );
            setChecklist(data.checklist || {});
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setBusy(null); }
    };

    const completed = PRODUCTION_STEP_KEYS.filter((s) => checklist[s] === true).length;
    const plan = c?.plan || {};
    const spec = c?.manufacturing_spec || {};
    const alignerSheet = useMaterialName(spec.aligner_sheet_material_id);
    const retainerSheet = useMaterialName(spec.retainer_sheet_material_id);

    // Status derivation
    const status =
        completed === 0 ? "not_started" :
        completed === PRODUCTION_STEP_KEYS.length ? "complete" : "in_progress";
    const statusLabel =
        status === "complete" ? "Complete" :
        status === "in_progress" ? "In Progress" : "Not Started";

    return (
        <SectionCard testId="fulfillment-production">
            <SectionHeader
                Icon={Factory}
                title="Production"
                status={status}
                statusLabel={statusLabel}
                testId="fulfillment-production"
            />
            <div className="p-5 space-y-5">
                {/* Planner spec (read-only upstream) */}
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">Planner spec (read-only)</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <KV label="Aligner sheet" value={alignerSheet || EMPTY} />
                        <KV label="Retainer sheet" value={retainerSheet || EMPTY} />
                        <KV label="Total aligners" value={`${plan.aligner_count_upper ?? "—"}U + ${plan.aligner_count_lower ?? "—"}L`} />
                        <KV label="Sheets per stage" value={spec.sheets_per_stage ?? "—"} />
                    </div>
                </div>

                {/* Live production data */}
                <div className="pt-4 border-t border-border">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">Live production data</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <KV label="Technician" value={c.production_tech_name || <span className="text-muted-foreground">Not assigned</span>} />
                        <KV label="Printer" value={c.production_printer_id || EMPTY} />
                        <KV label="Batch / lot" value={c.production_batch_number || EMPTY} />
                        <KV label="Completed at" value={c.production_completed_at ? fmtDateTime(c.production_completed_at) : EMPTY} />
                    </div>
                    {c.material_substitution_note && (
                        <div className="mt-3 p-2.5 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-xs flex items-start gap-2" data-testid="material-substitution-alert">
                            <Warning size={14} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                            <div>
                                <div className="font-bold">Material substitution flagged by technician</div>
                                <div className="mt-0.5 whitespace-pre-wrap">{c.material_substitution_note}</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Checklist */}
                <div className="pt-4 border-t border-border">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Production checklist (live)</div>
                        <span className="text-[10px] text-muted-foreground font-bold">{completed} / {PRODUCTION_STEP_KEYS.length} complete</span>
                    </div>
                    <ol className="space-y-1.5">
                        {PRODUCTION_STEP_KEYS.map((step, idx) => {
                            const done = checklist[step] === true;
                            return (
                                <li key={step}>
                                    <button
                                        onClick={() => toggle(step)}
                                        disabled={busy === step}
                                        className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg border transition ${done ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-border bg-card hover:bg-secondary"}`}
                                        data-testid={`fulfillment-prod-step-${step}${done ? "-done" : ""}`}
                                    >
                                        {done
                                            ? <CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0" />
                                            : <Circle size={16} className="text-muted-foreground shrink-0" />}
                                        <span className="text-xs text-muted-foreground font-mono">{idx + 1}.</span>
                                        <span className={`text-sm flex-1 ${done ? "line-through opacity-80" : ""}`}>{PRODUCTION_STEP_LABELS[step]}</span>
                                    </button>
                                </li>
                            );
                        })}
                    </ol>
                </div>
            </div>
        </SectionCard>
    );
}

/* ═══════════════════════════════════════════════════════════════════
   SECTION B — QA & QUALITY
   ═══════════════════════════════════════════════════════════════════ */
const QC_KEYS = ["aligner_fit", "trim_line", "surface_finish", "attachment_accuracy", "label_packaging"];

function QASection({ c, productionComplete }) {
    const [state, setState] = useState({ checklist: {}, result: null, failure_reason: "", re_inspection_required: false });
    const [reasonInput, setReasonInput] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get(`/admin/cases/${c.id}/qc-checklist`).then((r) => {
            setState(r.data);
            setReasonInput(r.data.failure_reason || "");
        }).catch(() => {});
    }, [c.id]);

    const setToggle = async (key, val) => {
        setBusy(true);
        try {
            const { data } = await api.put(`/admin/cases/${c.id}/qc-checklist`, { [key]: val });
            setState(data);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };
    const declareResult = async (result) => {
        if (result === "fail" && !reasonInput.trim()) {
            toast.error("Failure reason is required when failing QC");
            return;
        }
        setBusy(true);
        try {
            const body = { result };
            if (result === "fail") body.failure_reason = reasonInput.trim();
            const { data } = await api.put(`/admin/cases/${c.id}/qc-checklist`, body);
            setState(data);
            toast.success(`QC marked ${result.toUpperCase()}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    const allPassed = QC_KEYS.every((k) => state.checklist?.[k] === true);
    const status =
        state.result === "pass"        ? "complete" :
        state.result === "fail"        ? "failed"   :
        state.result === "conditional" ? "complete" :
        QC_KEYS.some((k) => state.checklist?.[k] !== undefined) ? "in_progress" :
        !productionComplete ? "awaiting" : "not_started";
    const statusLabel = {
        complete: "Complete",
        failed:   "Failed",
        in_progress: "In Progress",
        awaiting: "Awaiting production",
        not_started: "Not Started",
    }[status];

    return (
        <SectionCard testId="fulfillment-qa">
            <SectionHeader
                Icon={ShieldCheck}
                title="QA & Quality Check"
                status={status}
                statusLabel={statusLabel}
                testId="fulfillment-qa"
            />
            <div className="p-5 space-y-5">
                {/* Assignment */}
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">Assignment</div>
                    <div className="text-sm">
                        QA reviewer:{" "}
                        {c.qa_name
                            ? <span className="font-semibold">{c.qa_name}</span>
                            : <span className="text-rose-600">Not yet assigned — assign from the Overview tab</span>}
                        {state.completed_at && <span className="text-muted-foreground"> · completed {fmtDateTime(state.completed_at)}</span>}
                    </div>
                </div>

                {/* QC Checklist */}
                <div className="pt-4 border-t border-border">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">QC checklist</div>
                        <button
                            onClick={async () => {
                                const v = !state.re_inspection_required;
                                try {
                                    const { data } = await api.put(`/admin/cases/${c.id}/qc-checklist`, { re_inspection_required: v });
                                    setState(data);
                                } catch { toast.error("Failed"); }
                            }}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[10px] font-bold uppercase tracking-wider ${state.re_inspection_required ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-card text-muted-foreground border-border hover:bg-secondary"}`}
                            data-testid="qa-reinspection-flag"
                        >
                            <ArrowsClockwise size={10} /> Re-inspection {state.re_inspection_required ? "flagged" : "off"}
                        </button>
                    </div>
                    <div className="space-y-1.5">
                        {QC_KEYS.map((k) => {
                            const val = state.checklist?.[k];
                            return (
                                <div key={k} className="flex items-center justify-between px-3 py-2 rounded-lg border border-border bg-card" data-testid={`qa-toggle-${k}`}>
                                    <span className="text-sm font-medium">{QC_TOGGLE_LABELS[k]}</span>
                                    <div className="inline-flex items-center gap-1.5">
                                        <button onClick={() => setToggle(k, true)} disabled={busy}
                                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition ${val === true ? "bg-emerald-600 text-white" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"}`}
                                                data-testid={`qa-pass-${k}`}>
                                            <Check size={10} weight="bold" /> Pass
                                        </button>
                                        <button onClick={() => setToggle(k, false)} disabled={busy}
                                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition ${val === false ? "bg-rose-600 text-white" : "bg-rose-100 text-rose-700 hover:bg-rose-200"}`}
                                                data-testid={`qa-fail-${k}`}>
                                            <X size={10} weight="bold" /> Fail
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Overall result */}
                <div className="pt-4 border-t border-border">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">Overall result</div>
                    <div className="flex flex-wrap items-center gap-2">
                        <button onClick={() => declareResult("pass")} disabled={busy || !allPassed}
                                title={!allPassed ? "All 5 toggles must be marked Pass first" : "Mark QC pass"}
                                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition ${allPassed ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-emerald-600/30 text-white/80 cursor-not-allowed"}`}
                                data-testid="qa-result-pass">
                            <CheckCircle size={14} weight="fill" /> Pass
                        </button>
                        <button onClick={() => declareResult("conditional")} disabled={busy}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-amber-100 text-amber-800 hover:bg-amber-200"
                                data-testid="qa-result-conditional">
                            Conditional Pass
                        </button>
                        <button onClick={() => declareResult("fail")} disabled={busy}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-rose-100 text-rose-800 hover:bg-rose-200"
                                data-testid="qa-result-fail">
                            <Warning size={14} weight="fill" /> Fail
                        </button>
                    </div>
                    {(state.result === "fail" || QC_KEYS.some((k) => state.checklist?.[k] === false)) && (
                        <div className="mt-3">
                            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Failure reason</label>
                            <textarea value={reasonInput} onChange={(e) => setReasonInput(e.target.value)} rows={2}
                                placeholder="What went wrong, and what needs to happen next?"
                                className="w-full mt-1.5 p-3 text-sm rounded-lg border border-rose-300 bg-rose-50 placeholder-rose-400"
                                data-testid="qa-failure-reason" />
                        </div>
                    )}
                </div>
            </div>
        </SectionCard>
    );
}

/* ═══════════════════════════════════════════════════════════════════
   SECTION C — DISPATCH & SHIPMENT
   ═══════════════════════════════════════════════════════════════════ */
const COURIERS = [
    { value: "bluedart", label: "BlueDart" },
    { value: "dtdc", label: "DTDC" },
    { value: "delhivery", label: "Delhivery" },
    { value: "fedex", label: "FedEx" },
    { value: "dhl", label: "DHL" },
    { value: "indiapost", label: "India Post" },
    { value: "other", label: "Other" },
];
const SHIP_STATUSES = [
    { value: "preparing", label: "Preparing" },
    { value: "dispatched", label: "Dispatched" },
    { value: "in_transit", label: "In Transit" },
    { value: "delivered", label: "Delivered" },
];
const SHIP_STATUS_TONES = {
    preparing:  "bg-slate-100 text-slate-700",
    dispatched: "bg-sky-100 text-sky-700",
    in_transit: "bg-amber-100 text-amber-800",
    delivered:  "bg-emerald-100 text-emerald-800",
};

function DispatchSection({ c, qcComplete }) {
    const [shipments, setShipments] = useState([]);
    const [draftOpen, setDraftOpen] = useState(false);
    const [draft, setDraft] = useState({
        shipping_address: "", courier: "bluedart", tracking_number: "",
        dispatched_at: "", eta: "", status: "preparing", notes: "",
    });
    const [busy, setBusy] = useState(false);

    const fetch = () => {
        api.get(`/cases/${c.id}/shipments`).then((r) => setShipments(r.data || [])).catch(() => {});
    };
    useEffect(fetch, [c.id]);

    const openDraft = () => {
        setDraft((d) => ({ ...d, shipping_address: c.practice_location_address || c.doctor_clinic_name || "" }));
        setDraftOpen(true);
    };
    const create = async () => {
        if (!draft.tracking_number.trim() && draft.status !== "preparing") {
            toast.error("Tracking number required once dispatched");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/cases/${c.id}/shipments`, draft);
            toast.success("Shipment created");
            setDraftOpen(false);
            fetch();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };
    const updateField = async (sid, field, value) => {
        try {
            await api.put(`/cases/${c.id}/shipments/${sid}`, { [field]: value });
            fetch();
        } catch { toast.error("Failed"); }
    };

    const hasDelivered = shipments.some((s) => s.status === "delivered");
    const hasAny = shipments.length > 0;
    const status =
        hasDelivered ? "complete" :
        hasAny       ? "in_progress" :
        !qcComplete  ? "awaiting" : "not_started";
    const statusLabel = {
        complete: "Delivered",
        in_progress: "In Progress",
        awaiting: "Awaiting QC pass",
        not_started: "Not Started",
    }[status];

    return (
        <SectionCard testId="fulfillment-dispatch">
            <SectionHeader
                Icon={Truck}
                title="Dispatch & Shipment"
                status={status}
                statusLabel={statusLabel}
                testId="fulfillment-dispatch"
            />
            <div className="p-5 space-y-4">
                <div className="flex justify-end">
                    <button onClick={openDraft}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary text-white text-[10px] font-bold uppercase tracking-wider"
                            data-testid="shipments-add-toggle">
                        <Plus size={11} weight="bold" /> New shipment
                    </button>
                </div>

                {draftOpen && (
                    <div className="p-4 rounded-lg border border-primary/30 bg-primary/5 space-y-3" data-testid="shipments-draft">
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Shipping address</label>
                            <textarea value={draft.shipping_address}
                                onChange={(e) => setDraft({ ...draft, shipping_address: e.target.value })}
                                rows={2} placeholder="Clinic address (auto-filled, editable)"
                                className="w-full mt-1 p-2 text-sm rounded-lg border border-border bg-background"
                                data-testid="shipments-draft-address" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Courier</label>
                                <select value={draft.courier} onChange={(e) => setDraft({ ...draft, courier: e.target.value })}
                                    className="w-full mt-1 h-9 text-sm rounded-lg border border-border bg-background px-2"
                                    data-testid="shipments-draft-courier">
                                    {COURIERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Tracking number</label>
                                <input value={draft.tracking_number} onChange={(e) => setDraft({ ...draft, tracking_number: e.target.value })}
                                    className="w-full mt-1 h-9 text-sm rounded-lg border border-border bg-background px-2"
                                    data-testid="shipments-draft-tracking" />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Dispatched on</label>
                                <input type="date" value={draft.dispatched_at} onChange={(e) => setDraft({ ...draft, dispatched_at: e.target.value })}
                                    className="w-full mt-1 h-9 text-sm rounded-lg border border-border bg-background px-2" />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">ETA</label>
                                <input type="date" value={draft.eta} onChange={(e) => setDraft({ ...draft, eta: e.target.value })}
                                    className="w-full mt-1 h-9 text-sm rounded-lg border border-border bg-background px-2" />
                            </div>
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Status</label>
                            <div className="inline-flex w-full mt-1 rounded-full border border-border bg-card overflow-hidden">
                                {SHIP_STATUSES.map((s) => (
                                    <button key={s.value} onClick={() => setDraft({ ...draft, status: s.value })}
                                        className={`flex-1 py-1.5 text-xs font-semibold ${draft.status === s.value ? "bg-foreground text-white" : "text-muted-foreground hover:bg-secondary"}`}>
                                        {s.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={create} disabled={busy} className="flex-1 py-2 text-sm font-bold rounded-lg bg-primary text-white" data-testid="shipments-draft-save">Save shipment</button>
                            <button onClick={() => setDraftOpen(false)} className="flex-1 py-2 text-sm font-bold rounded-lg border border-border">Cancel</button>
                        </div>
                    </div>
                )}

                {shipments.length === 0 && !draftOpen ? (
                    <div className="py-8 text-center">
                        <Truck size={28} className="mx-auto text-muted-foreground/40 mb-2" />
                        <div className="text-sm text-muted-foreground">No shipments yet. Create one once QC passes.</div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {shipments.map((s) => (
                            <div key={s.id} className="p-3 rounded-lg border border-border bg-card" data-testid={`shipment-${s.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${SHIP_STATUS_TONES[s.status] || "bg-slate-100 text-slate-700"}`}>{humanize(s.status)}</span>
                                            <span className="text-xs text-muted-foreground">{(COURIERS.find((cc) => cc.value === s.courier) || {}).label || humanize(s.courier) || EMPTY}</span>
                                        </div>
                                        <div className="mt-1.5 text-sm">
                                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Tracking · </span>
                                            <span className="font-mono">{s.tracking_number || EMPTY}</span>
                                            {s.tracking_number && (
                                                <a href={`https://www.google.com/search?q=${encodeURIComponent(s.tracking_number)}+tracking`} target="_blank" rel="noreferrer"
                                                   className="ml-1 text-primary inline-flex items-center gap-0.5 text-xs">
                                                    <ArrowSquareOut size={11} />
                                                </a>
                                            )}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{s.shipping_address || EMPTY}</div>
                                    </div>
                                    <div className="text-right text-xs shrink-0">
                                        {s.dispatched_at && <div>Dispatched: {fmtDate(s.dispatched_at)}</div>}
                                        {s.eta && <div className="text-muted-foreground">ETA: {fmtDate(s.eta)}</div>}
                                    </div>
                                </div>
                                <div className="mt-3 pt-2 border-t border-border flex items-center gap-1.5 flex-wrap">
                                    <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">Update:</span>
                                    {SHIP_STATUSES.map((st) => (
                                        <button key={st.value} onClick={() => updateField(s.id, "status", st.value)}
                                            className={`px-2 py-0.5 rounded text-[10px] font-semibold transition ${s.status === st.value ? "bg-foreground text-white" : "bg-secondary text-muted-foreground hover:bg-secondary/70"}`}
                                            data-testid={`shipment-${s.id}-status-${st.value}`}>
                                            {st.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </SectionCard>
    );
}

/* ─── Utility ─────────────────────────────────────────────────────── */
function KV({ label, value }) {
    return (
        <div>
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">{label}</div>
            <div className="mt-0.5">{value || <span className="text-muted-foreground">{EMPTY}</span>}</div>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════
   FULFILLMENT TAB ENTRY POINT — composes Production → QA → Dispatch
   ═══════════════════════════════════════════════════════════════════ */
export function FulfillmentTab({ c }) {
    // Cross-section status hooks: production complete means we can let QA
    // act; QC complete means dispatch can act. We use a lightweight
    // per-section fetch; the API costs are negligible.
    const [productionComplete, setProductionComplete] = useState(false);
    const [qcComplete, setQcComplete] = useState(false);

    useEffect(() => {
        api.get(`/admin/cases/${c.id}/production-checklist`)
            .then((r) => setProductionComplete(
                ["ready_for_qc","print_completed","trim_and_finish","stl_files_received","files_verified","material_pulled","print_started"]
                    .every((k) => r.data.checklist?.[k] === true)
            )).catch(() => {});
        api.get(`/admin/cases/${c.id}/qc-checklist`)
            .then((r) => setQcComplete(r.data.result === "pass" || r.data.result === "conditional"))
            .catch(() => {});
    }, [c.id]);

    return (
        <div className="space-y-5" data-testid="fulfillment-tab">
            <ProductionSection c={c} />
            <QASection c={c} productionComplete={productionComplete} />
            <DispatchSection c={c} qcComplete={qcComplete} />
        </div>
    );
}
